import React, { useState } from 'react';
import moment from 'moment';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

const columnWidths = ['11%', '9%', '10%', '8%', '10%', '8%', '8%', '8%', '7%', '15%', '6%'];

const ColGroup = () => (
    <colgroup>
        {columnWidths.map((width, i) => (
            <col key={i} style={{ width }} />
        ))}
    </colgroup>
);

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

const formatTime = (isoString) => {
    if (!isoString) return '-';
    const m = moment(isoString);
    return m.isValid() ? m.format('HH:mm') : '-';
};

const formatRoute = (origin, destination) => {
    if (!origin && !destination) return '-';
    return `${origin || '?'} → ${destination || '?'}`;
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

const TransferOverrideModal = ({ overrideContext, open, onClose, onApply, isPatching }) => {
    const [action, setAction] = useState('pass');
    const [reason, setReason] = useState('');

    React.useEffect(() => {
        if (open && overrideContext) {
            setAction('pass');
            setReason('');
        }
    }, [open, overrideContext]);

    if (!open) return null;

    const trimmedReason = reason?.trim();
    const canApply = !!trimmedReason;

    const handleApply = () => {
        if (!overrideContext || !onApply || !trimmedReason) return;
        if (action === 'ignore') {
            onApply(overrideContext.row, null, trimmedReason, { ignore: true });
        } else {
            onApply(overrideContext.row, action === 'pass', trimmedReason);
        }
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="transfer-override-title">
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 id="transfer-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Transfer SLA Override
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    Manually set pass, fail, or ignore for {overrideContext?.row?.passengerName || 'this passenger'}
                    {overrideContext?.kpiKey ? ` (${overrideContext.kpiKey === 'preAdvised' ? 'Pre-Advised' : 'Non Pre-Advised'})` : ''}.
                </p>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'pass'} onChange={() => setAction('pass')} />
                        Auto Pass (Manual Pass)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'fail'} onChange={() => setAction('fail')} />
                        Auto Fail (Manual Fail)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={action === 'ignore'} onChange={() => setAction('ignore')} />
                        Ignore (Exclude from SLA)
                    </label>
                    <div style={{ marginTop: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            Reason (required)
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={action === 'ignore' ? 'e.g. Not applicable to SLA' : action === 'pass' ? 'e.g. Within MCT' : 'e.g. Resource never reached pax'}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={isPatching || !canApply}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.8 : 1,
                        }}
                    >
                        {isPatching ? 'Updating...' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const KpiSection = ({ title, kpiKey, rawData, date, onOpenOverrideModal }) => {
    const [isOpen, setIsOpen] = useState(false);
    const rows = rawData || [];

    if (!rows.length) {
        return (
            <div>
                <p style={subTitleStyle}>{title} {date ? `(${date})` : ''}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No records.</p>
            </div>
        );
    }

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '12px', backgroundColor: '#fff' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>{title} ({rows.length}) {date ? `(${date})` : ''}</p>
            </div>

            {!isOpen ? null : (<>
                <table style={{ ...tableStyle, borderBottom: 'none', marginTop: '10px' }}>
                    <ColGroup />
                    <thead>
                        <tr>
                            <th style={headerStyle}>Passenger</th>
                            <th style={headerStyle}>Assistance</th>
                            <th style={headerStyle}>Inbound</th>
                            <th style={headerStyle}>Inbound Date</th>
                            <th style={headerStyle}>Outbound</th>
                            <th style={headerStyle}>Outbound Route</th>
                            <th style={headerStyle}>Inbound At Loc</th>
                            <th style={headerStyle}>Boarding At Loc</th>
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Pass</th>
                            <th style={headerStyle}>Reason</th>
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                </table>
                <div style={{ maxHeight: '400px', overflowY: 'auto', borderTop: 'none' }}>
                    <table style={tableStyle}>
                        <ColGroup />
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={row.bookingId || idx}>
                                    <td style={cellStyle}>{row.passengerName ?? '–'}</td>
                                    <td style={cellStyle}>{row.assistance ?? '–'}</td>
                                    <td style={cellStyle}>
                                        {row.inboundFlight || '-'}
                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {formatRoute(row.inboundOrigin, row.inboundDestination)}
                                        </div>
                                        {row.pairedInboundId
                                            ? <div style={{ fontSize: '10px', color: '#16a34a' }}>paired</div>
                                            : <div style={{ fontSize: '10px', color: '#dc2626' }}>not paired</div>
                                        }
                                    </td>
                                    <td style={cellStyle}>{row.inboundDepartureDate || '-'}</td>
                                    <td style={cellStyle}>
                                        {row.outboundFlight || '-'}
                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {formatTime(row.outboundDepartureTime)}
                                        </div>
                                    </td>
                                    <td style={cellStyle}>{formatRoute(row.outboundOrigin, row.outboundDestination)}</td>
                                    <td style={{
                                        ...cellStyle,
                                        color: row.inboundAssisted ? '#166534' : '#6b7280',
                                    }}>{formatTime(row.inboundAtLocation)}</td>
                                    <td style={{
                                        ...cellStyle,
                                        color: row.outboundAssisted ? '#166534' : '#6b7280',
                                    }}>{formatTime(row.outboundAtLocation)}</td>
                                    <td style={{
                                        ...cellStyle,
                                        textAlign: 'center',
                                        backgroundColor: row.isIgnored ? '#fef9c3' : row.pass ? '#dcfce7' : '#fee2e2',
                                        color: row.isIgnored ? '#854d0e' : row.pass ? '#166534' : '#991b1b',
                                    }}>
                                        {row.isIgnored
                                            ? 'Ignored'
                                            : row.pass
                                                ? (row.isManualOverride ? 'Pass (Manual)' : 'Pass')
                                                : (row.isManualOverride ? 'Fail (Manual)' : 'Fail')}
                                    </td>
                                    <td style={cellStyle}>
                                        {row.passReason || '-'}
                                        {row.paxRejectionReason && (
                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{row.paxRejectionReason}</div>
                                        )}
                                    </td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                                        {onOpenOverrideModal && row.bookingId ? (
                                            <button
                                                type="button"
                                                onClick={() => onOpenOverrideModal(row, date, kpiKey)}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#3b82f6',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                Action
                                            </button>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>)}
        </div>
    );
};

const SECTION_TITLES = {
    preAdvised: 'Pre-Advised Transfer',
    nonPreAdvised: 'Non Pre-Advised Transfer',
};

const TransferSlaRawDataTable = ({ dailyReports, overrideContext, onOverrideRowChange, onPatchTransfer, isPatching }) => {
    if (!dailyReports?.length) return null;

    const handleOpenOverrideModal = (row, operationDate, kpiKey) => {
        onOverrideRowChange?.({ row, operationDate, kpiKey });
    };

    return (
        <div style={{ marginTop: '32px' }}>
            {dailyReports.map((report) => {
                const kpiKeys = report.kpis ? Object.keys(report.kpis) : [];
                return (
                    <div key={report.date} style={{ marginBottom: '24px' }}>
                        {kpiKeys.map((key) => {
                            const kpi = report.kpis[key];
                            return (
                                <KpiSection
                                    key={key}
                                    kpiKey={key}
                                    title={kpi.title || SECTION_TITLES[key] || key}
                                    rawData={kpi.rawData}
                                    date={report.date}
                                    onOpenOverrideModal={handleOpenOverrideModal}
                                />
                            );
                        })}
                    </div>
                );
            })}
            <TransferOverrideModal
                overrideContext={overrideContext}
                open={!!overrideContext}
                onClose={() => onOverrideRowChange?.(null)}
                onApply={onPatchTransfer}
                isPatching={isPatching}
            />
        </div>
    );
};

export default TransferSlaRawDataTable;
